/**
 * Highlights (metric 25): parse LLM plain-text blocks into structured sections for Welcome UI.
 * Matches mockup: numbered title → bullet evidence → Ne anlama geliyor? → Aksiyon:
 */

import React from "react";
import styles from "./style.module.scss";

/** Split combined narrative into insight blocks (lines starting with "N) "). */
export function splitHighlightsInsightBlocks(text) {
  const t = String(text || "").trim();
  if (!t) return [];
  return t
    .split(/\n(?=\s*\d+\)\s)/)
    .map((p) => p.trim())
    .filter(Boolean);
}

/**
 * Parse one block into { title, evidence[], meaning, action }.
 * Tolerates missing numbers or partial structure.
 */
/**
 * Map one LLM JSON item to the internal insight shape (evidence / meaning / action).
 */
function insightFromJsonItem(it) {
  if (!it || typeof it !== "object") return null;
  const titleRaw = it.title ?? it.headline ?? "";
  const titleDisplay = String(titleRaw).trim() || "—";

  let evidence = [];
  if (Array.isArray(it.evidence)) {
    evidence = it.evidence.map((x) => String(x).trim()).filter(Boolean);
  } else if (typeof it.evidence === "string" && it.evidence.trim()) {
    evidence = it.evidence
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean);
  } else if (Array.isArray(it.bullets)) {
    evidence = it.bullets.map((x) => String(x).trim()).filter(Boolean);
  } else if (typeof it.description === "string" && it.description.trim()) {
    const desc = it.description.trim();
    if (!desc.startsWith("{")) {
      evidence = desc
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter(Boolean);
    }
  }

  const meaning = String(
    it.meaning ?? it.interpretation ?? it.ne_anlama_geliyor ?? "",
  ).trim();

  let action = "";
  if (Array.isArray(it.action)) {
    action = it.action.map((x) => String(x).trim()).filter(Boolean).join(" ");
  } else if (typeof it.action === "string") {
    action = it.action.trim();
  } else if (it.action != null) {
    action = String(it.action).trim();
  }

  return {
    title: titleDisplay,
    evidence,
    meaning,
    action,
  };
}

/** Editable triplet aligned with Highlights dashboard sections (critical / meaning / action). */
export function extractHighlightEditingFields(it) {
  if (!it || typeof it !== "object") {
    return { critical: "", meaning: "", action: "" };
  }
  const shaped = insightFromJsonItem(it);
  if (!shaped) return { critical: "", meaning: "", action: "" };
  const critical = Array.isArray(shaped.evidence)
    ? shaped.evidence.map((x) => String(x).trim()).filter(Boolean).join("\n")
    : "";
  return {
    critical,
    meaning: String(shaped.meaning || "").trim(),
    action: String(shaped.action || "").trim(),
  };
}

/** Persists edits as `description` + `meaning` + `action` and drops superseded bullet fields so the viewer reads consistent text. */
export function applyHighlightEditingFieldsToItem(item, fields) {
  const base =
    item && typeof item === "object"
      ? JSON.parse(JSON.stringify(item))
      : {};
  const critical = String(fields?.critical ?? "").trim();
  const meaning = String(fields?.meaning ?? "").trim();
  const action = String(fields?.action ?? "").trim();

  delete base.evidence;
  delete base.bullets;
  base.meaning = meaning;
  base.action = action;
  base.description = critical;

  return base;
}

/**
 * Parses a hierarchical tab JSON body for Highlights (metric 25) editing.
 * Accepts `{ "items": [...] }` or a top-level highlights array (worker shape).
 */
export function parseHighlightTabContentForEdit(contentStr) {
  const t = String(contentStr ?? "").trim();
  if (!t) {
    return { ok: false, reason: "empty" };
  }
  try {
    const data = JSON.parse(t);
    let rootDoc;
    if (Array.isArray(data)) {
      rootDoc = { items: data };
    } else if (
      data &&
      typeof data === "object" &&
      Array.isArray(data.items)
    ) {
      rootDoc = JSON.parse(JSON.stringify(data));
    } else {
      return { ok: false, reason: "bad_shape" };
    }
    const rows = rootDoc.items.map((row) =>
      extractHighlightEditingFields(row),
    );
    return { ok: true, rootDoc, rows };
  } catch {
    return { ok: false, reason: "json" };
  }
}

export function serializeHighlightTabFromEditDraft(rootDoc, rows) {
  const src = JSON.parse(JSON.stringify(rootDoc));
  const prevItems = Array.isArray(src.items) ? src.items : [];
  const nextItems = prevItems.map((it, idx) => {
    const fields = rows[idx] ?? extractHighlightEditingFields(it);
    return applyHighlightEditingFieldsToItem(it, fields);
  });
  src.items = nextItems;
  return JSON.stringify(src);
}

/**
 * Recover highlight cards when worker stored a nested JSON blob inside description.
 */
export function normalizeHighlightsWidgetItems(items) {
  const arr = Array.isArray(items) ? items : [];
  const expanded = [];
  for (const it of arr) {
    if (!it || typeof it !== "object") continue;
    const desc = String(it.description ?? "").trim();
    const title = String(it.title ?? "").trim();
    const looksWrapped =
      desc.startsWith("{") ||
      ((title === "Özet" || title === "Summary") && desc.length > 200);
    if (looksWrapped) {
      try {
        const inner = JSON.parse(desc);
        const innerItems = Array.isArray(inner)
          ? inner
          : Array.isArray(inner?.items)
            ? inner.items
            : null;
        if (innerItems?.length) {
          for (const row of innerItems) {
            if (row && typeof row === "object") expanded.push(row);
          }
          continue;
        }
      } catch {
        // truncated / invalid nested JSON — drop wrapper
      }
      continue;
    }
    if (String(it.highlight_id ?? "").trim()) {
      expanded.push(it);
    }
  }
  return expanded;
}

/**
 * Build structured insights directly from widget `data.items` rows (after segment/theme filter).
 */
export function parseInsightsFromWidgetItems(items) {
  const arr = normalizeHighlightsWidgetItems(items);
  const insights = arr.map(insightFromJsonItem).filter(Boolean);
  if (insights.length === 0) return null;
  const hasStructure = insights.some(
    (ins) =>
      (ins.evidence && ins.evidence.length > 0) ||
      ins.meaning ||
      ins.action ||
      (ins.title && String(ins.title).trim() && ins.title !== "—"),
  );
  if (!hasStructure) return null;
  return { mode: "structured", insights };
}

function tryParseJsonInsights(text) {
  const t = String(text || "").trim();
  if (!t) return null;
  const first = t[0];
  if (first !== "{" && first !== "[") return null;
  try {
    const data = JSON.parse(t);
    const arr = Array.isArray(data) ? data : data.items;
    if (!Array.isArray(arr) || arr.length === 0) return null;
    const insights = arr.map(insightFromJsonItem).filter(Boolean);
    const hasStructure = insights.some(
      (ins) =>
        (ins.evidence && ins.evidence.length > 0) ||
        ins.meaning ||
        ins.action ||
        (ins.title && String(ins.title).trim() && ins.title !== "—"),
    );
    if (!hasStructure) return null;
    return { mode: "structured", insights };
  } catch {
    return null;
  }
}

export function parseHighlightsInsightBlock(block) {
  const raw = String(block || "").trim();
  if (!raw) return null;

  const lines = raw.split(/\r?\n/).map((l) => l.trim());
  const line0 = lines[0] || "";
  const titleMatch = line0.match(/^(\d+)\)\s*(.+)$/);
  const titleDisplay = titleMatch
    ? `${titleMatch[1]}) ${titleMatch[2].trim()}`
    : line0;

  let i = 1;
  const evidence = [];
  let meaning = "";
  let action = "";

  while (i < lines.length) {
    const line = lines[i];
    if (!line) {
      i += 1;
      continue;
    }
    if (/^ne\s+anlama\s+geliyor/i.test(line)) {
      meaning = line.replace(/^ne\s+anlama\s+geliyor\?\s*/i, "").trim();
      i += 1;
      break;
    }
    if (/^aksiyon\s*:/i.test(line)) {
      action = line.replace(/^aksiyon\s*:\s*/i, "").trim();
      i += 1;
      break;
    }
    if (/^[-•*]\s/.test(line)) {
      evidence.push(line.replace(/^[-•*]\s*/, ""));
    } else {
      evidence.push(line);
    }
    i += 1;
  }

  while (i < lines.length) {
    const line = lines[i];
    if (!line) {
      i += 1;
      continue;
    }
    if (/^aksiyon\s*:/i.test(line)) {
      action = line.replace(/^aksiyon\s*:\s*/i, "").trim();
      i += 1;
      break;
    }
    meaning += (meaning ? " " : "") + line;
    i += 1;
  }

  while (i < lines.length) {
    const line = lines[i];
    if (line) action += (action ? " " : "") + line;
    i += 1;
  }

  return {
    title: titleDisplay,
    evidence,
    meaning: meaning.trim(),
    action: action.trim(),
  };
}

export function parseHighlightsNarrative(text) {
  const t = String(text || "").trim();
  if (!t) return { mode: "empty" };

  const jsonParsed = tryParseJsonInsights(t);
  if (jsonParsed) return jsonParsed;

  const blocks = splitHighlightsInsightBlocks(t);
  const toParse = blocks.length > 0 ? blocks : [t];
  const insights = toParse.map(parseHighlightsInsightBlock).filter(Boolean);

  const hasStructure = insights.some(
    (ins) =>
      (ins.evidence && ins.evidence.length > 0) ||
      ins.meaning ||
      ins.action ||
      /\d+\)\s/.test(ins.title || ""),
  );

  if (!hasStructure) {
    return { mode: "plain", text: t };
  }

  return { mode: "structured", insights };
}

/** Hierarchical tab panel: clamp + read-more wrapper uses this inner tree. */
export function WelcomeHighlightsStructuredInner({ insights }) {
  if (!insights || insights.length === 0) return null;
  return (
    <div className={styles.highlightsInsightList}>
      {insights.map((ins, idx) => (
        <section
          key={`hl-${idx}-${ins.title?.slice(0, 24) || idx}`}
          className={styles.highlightsInsight}
        >
          {ins.title ? (
            <h3 className={styles.highlightsInsightTitle}>{ins.title}</h3>
          ) : null}
          {Array.isArray(ins.evidence) && ins.evidence.length > 0 ? (
            <ul className={styles.highlightsInsightEvidence}>
              {ins.evidence.map((ev, j) => (
                <li key={`hl-ev-${idx}-${j}`}>{ev}</li>
              ))}
            </ul>
          ) : null}
          {ins.meaning ? (
            <div className={styles.highlightsInsightSection}>
              <span className={styles.highlightsInsightLabel}>
                Ne anlama geliyor?
              </span>
              <p className={styles.highlightsInsightPara}>{ins.meaning}</p>
            </div>
          ) : null}
          {ins.action ? (
            <div className={styles.highlightsInsightSection}>
              <span className={styles.highlightsInsightLabel}>Aksiyon:</span>
              <p className={styles.highlightsInsightPara}>{ins.action}</p>
            </div>
          ) : null}
        </section>
      ))}
    </div>
  );
}

export function WelcomeHighlightsStructuredBody({ bodyText }) {
  const parsed = parseHighlightsNarrative(bodyText);
  if (parsed.mode === "empty") {
    return null;
  }
  if (parsed.mode === "plain") {
    return (
      <div className={styles.highlightsInsightPlainFallback}>{parsed.text}</div>
    );
  }
  return <WelcomeHighlightsStructuredInner insights={parsed.insights} />;
}
